// Logic THUẦN của trang Gom Đơn /inbox (không React, không gọi mạng) — test ở tests/unit/web-gomdon-inbox.test.ts.
// Port hành vi từ gomdon/web/src/pages/Inbox.tsx + components/ChatView.tsx.

import type { ChannelType, Conversation, ConversationKind, Message, ReplyWindow } from './types'

/** Lọc loại trên trang Gom Đơn: '' = Tất cả, `chat` = Tin nhắn, `comment` = Bình luận. Lưu trên URL `?kind=` */
export type KindFilter = ConversationKind | ''

export const KIND_LABEL: Record<ConversationKind, string> = { chat: 'Tin nhắn', comment: 'Bình luận' }

/** Đọc `?kind=` (giá trị lạ → Tất cả) */
export function parseKind(v: string | null): KindFilter {
  return v === 'chat' || v === 'comment' ? v : ''
}

/** Đường dẫn trang Gom Đơn: danh sách hoặc một hội thoại, giữ bộ lọc loại */
export function inboxHref(id: number | null, kind: KindFilter): string {
  return `/inbox${id !== null ? `/${id}` : ''}${kind ? `?kind=${kind}` : ''}`
}

/** Hội thoại không bao giờ hiện ở trang Gom Đơn: kênh form (đơn từ landing page) */
export function isInboxConversation(c: Pick<Conversation, 'channelType'>, reviewMode: boolean): boolean {
  if (c.channelType === 'webform') return false
  // Shop reviewer Meta: không có Zalo cá nhân ở bất kỳ đâu
  if (reviewMode && c.channelType === 'zalo_personal') return false
  return true
}

export interface ChannelFilter {
  key: ChannelType | ''
  label: string
}

/** Lọc nền tảng: Bình luận chỉ có Facebook / Instagram; Zalo ẩn khi shop reviewer Meta */
export function channelFilters(kind: KindFilter, showZalo: boolean): ChannelFilter[] {
  const all: ChannelFilter = { key: '', label: 'Tất cả' }
  if (kind === 'comment') return [all, { key: 'facebook', label: 'Facebook' }, { key: 'instagram', label: 'Instagram' }]
  return [all, ...(showZalo ? [{ key: 'zalo_personal' as const, label: 'Zalo' }] : []), { key: 'facebook', label: 'Facebook' }, { key: 'instagram', label: 'Instagram' }]
}

const byRecent = (a: Conversation, b: Conversation) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0) || b.id - a.id

/** Danh sách hiện ở trang: đúng loại (hoặc mọi loại), đúng kênh lọc, khớp ô tìm (tên, tiêu đề, tin cuối), mới nhất trước */
export function visibleConversations(
  list: readonly Conversation[],
  opts: { kind: KindFilter; channel: ChannelType | ''; q: string; reviewMode: boolean },
): Conversation[] {
  const q = opts.q.trim().toLowerCase()
  return list
    .filter(
      (c) =>
        (!opts.kind || c.kind === opts.kind) &&
        isInboxConversation(c, opts.reviewMode) &&
        (!opts.channel || c.channelType === opts.channel) &&
        (!q || [c.contactName, c.title, c.lastMessageText].some((s) => s?.toLowerCase().includes(q))),
    )
    .sort(byRecent)
}

/** Thêm / thay một hội thoại (từ socket) vào danh sách, giữ thứ tự mới nhất trước. Kênh form không vào. */
export function upsertConversation(list: readonly Conversation[] | undefined, c: Conversation): Conversation[] | undefined {
  if (!list) return list
  if (c.channelType === 'webform') return list as Conversation[]
  return [...list.filter((x) => x.id !== c.id), c].sort(byRecent)
}

/** Số hội thoại còn tin chưa đọc theo loại (badge menu = tổng, số trên dải lọc loại) */
export function unreadByKind(list: readonly Conversation[] | undefined, reviewMode: boolean): Record<ConversationKind, number> {
  const out: Record<ConversationKind, number> = { chat: 0, comment: 0 }
  for (const c of list ?? []) if (c.unreadCount > 0 && isInboxConversation(c, reviewMode) && c.kind in out) out[c.kind] += 1
  return out
}

/** Số hội thoại chưa đọc theo kênh, trong loại đang lọc ('' = mọi loại) — số trên nút lọc nền tảng */
export function unreadByChannel(list: readonly Conversation[] | undefined, kind: KindFilter, reviewMode: boolean): Partial<Record<ChannelType, number>> {
  const out: Partial<Record<ChannelType, number>> = {}
  for (const c of list ?? []) if ((!kind || c.kind === kind) && c.unreadCount > 0 && isInboxConversation(c, reviewMode)) out[c.channelType] = (out[c.channelType] ?? 0) + 1
  return out
}

/** Thêm tin vào khung chat nếu chưa có (socket và kết quả POST có thể cùng về) */
export function appendMessage(messages: readonly Message[], m: Message): Message[] {
  return messages.some((x) => x.id === m.id) ? (messages as Message[]) : [...messages, m]
}

export function replaceMessage(messages: readonly Message[], m: Message): Message[] {
  return messages.map((x) => (x.id === m.id ? m : x))
}

// ---- Luật 24 giờ / Human Agent (useReplyWindow của Gom Đơn) ----

export type ReplyWindowState = { mode: 'open' | 'agent'; left: number } | { mode: 'closed'; left: 0 }

/** null = không giới hạn (Zalo, bình luận, chưa có tin của khách) */
export function replyWindowState(w: ReplyWindow | null, now: number): ReplyWindowState | null {
  if (!w) return null
  if (now <= w.standardUntil) return { mode: 'open', left: w.standardUntil - now }
  if (w.humanAgentUntil && now <= w.humanAgentUntil) return { mode: 'agent', left: w.humanAgentUntil - now }
  return { mode: 'closed', left: 0 }
}

/** Thời gian còn lại dạng "5 giờ 12 phút" */
export function fmtLeft(ms: number): string {
  const total = Math.max(1, Math.floor(ms / 60_000))
  const d = Math.floor(total / 1440)
  const h = Math.floor((total % 1440) / 60)
  const m = total % 60
  if (d) return `${d} ngày${h ? ` ${h} giờ` : ''}`
  if (h) return `${h} giờ${m ? ` ${m} phút` : ''}`
  return `${m} phút`
}

export type ComposerHint = { tone: 'muted' | 'warn' | 'danger' | 'info'; icon: 'lock' | 'note' | 'clock'; text: string }

/** Dòng gợi ý trên ô trả lời — đúng câu chữ và thứ tự ưu tiên của ChatView Gom Đơn */
export function composerHint(c: Pick<Conversation, 'kind' | 'channelType' | 'canSend' | 'replyWindow'>, win: ReplyWindowState | null): ComposerHint | null {
  const platform = c.channelType === 'instagram' ? 'Instagram' : 'Facebook'
  // Bình luận: không có dòng gợi ý (tên 2 nút hiện khi rê chuột)
  if (c.kind === 'comment') return null
  if (!c.canSend) return { tone: 'muted', icon: 'note', text: 'Kênh này không gửi tin ra ngoài được, nội dung chỉ lưu làm ghi chú nội bộ.' }
  if (win?.mode === 'closed') {
    return {
      tone: 'danger',
      icon: 'lock',
      text: `Đã quá ${c.replyWindow?.humanAgentUntil ? '7 ngày' : '24 giờ'} kể từ tin cuối của khách nên ${platform} không cho gửi. Khi khách nhắn lại, ô trả lời sẽ mở.`,
    }
  }
  // Có Human Agent: trả lời được suốt 7 ngày (quá 24 giờ engine tự gắn tag HUMAN_AGENT), không nhắc gì cho tới khi khoá
  if (c.replyWindow?.humanAgentUntil) return null
  if (win?.mode === 'open') {
    return {
      tone: win.left < 3 * 3600_000 ? 'warn' : 'muted',
      icon: 'clock',
      text: `Còn ${fmtLeft(win.left)} để trả lời (${platform} chỉ cho nhắn trong 24 giờ kể từ tin cuối của khách).`,
    }
  }
  return null
}

// ---- Hiển thị tin ----

/** Engine lưu tin "Trả lời riêng" với tiền tố này trong nội dung; web tách ra để hiện nhãn (icon lucide) thay cho emoji */
const PRIVATE_PREFIX = /^🔒\s*\[Trả lời riêng\]\s*/u

export function splitPrivateReply(text: string | null): { private: boolean; text: string } {
  const t = text ?? ''
  const m = PRIVATE_PREFIX.exec(t)
  return m ? { private: true, text: t.slice(m[0].length) } : { private: false, text: t }
}

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/** Nhãn ngày chia khung chat: Hôm nay / Hôm qua / "thứ hai, 27/10" */
export function dayLabel(ts: number, now = Date.now()): string {
  const d = new Date(ts)
  if (sameDay(d, new Date(now))) return 'Hôm nay'
  if (sameDay(d, new Date(now - 86_400_000))) return 'Hôm qua'
  return d.toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit' })
}

export function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
}

/** Thời điểm tin cuối ở danh sách: "Vừa xong", "5 phút", "3 giờ", "Hôm qua", "27/10" */
export function timeAgo(ts: number | null, now = Date.now()): string {
  if (!ts) return ''
  const s = Math.max(0, Math.floor((now - ts) / 1000))
  if (s < 60) return 'Vừa xong'
  if (s < 3600) return `${Math.floor(s / 60)} phút`
  if (s < 86_400 && sameDay(new Date(ts), new Date(now))) return `${Math.floor(s / 3600)} giờ`
  if (sameDay(new Date(ts), new Date(now - 86_400_000))) return 'Hôm qua'
  return new Date(ts).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
}

/** Địa chỉ bóc được: có nguyên văn `full` thì dùng, không thì ghép số nhà / xã / tỉnh */
export function addressText(a: { street: string | null; ward: string | null; province: string | null; full: string | null }): string | null {
  const parts = [a.street, a.ward, a.province].filter((s): s is string => !!s && !!s.trim())
  if (a.full && a.full.trim()) return a.full.trim()
  return parts.length ? parts.join(', ') : null
}

// ---- An toàn cho dữ liệu từ engine (tin khách, hồ sơ nền tảng) ----

/**
 * URL dùng được cho href / src: chỉ http(s) tuyệt đối. Còn lại (javascript:, data:, vbscript:, tương đối, không đọc được) → null,
 * KHÔNG render link. React 18 vẫn chạy `javascript:` khi bấm, mà URL tệp đính kèm Zalo đến thẳng từ tin của khách.
 */
/** Tệp Gom Đơn đã gửi (engine phục vụ ở /gomdon/media/<tên>, cùng nguồn với web) */
const GOMDON_MEDIA_PATH = /^\/gomdon\/media\/[A-Za-z0-9_-]+\.[a-z0-9]+$/

export function safeUrl(u: unknown): string | null {
  if (typeof u !== 'string' || !u.trim()) return null
  if (GOMDON_MEDIA_PATH.test(u.trim())) return u.trim()
  try {
    const parsed = new URL(u.trim())
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null
  } catch {
    return null
  }
}

/** Ảnh: như safeUrl, thêm ảnh nhúng base64 (QR đăng nhập Zalo do engine tạo) đúng định dạng ảnh raster */
export function safeImageSrc(u: unknown): string | null {
  if (typeof u === 'string' && /^data:image\/(png|jpeg|gif|webp);base64,[A-Za-z0-9+/=]+$/.test(u)) return u
  return safeUrl(u)
}

/**
 * Hội thoại đang mở luôn coi là đã đọc ở danh sách: lần hỏi lại định kỳ / khi quay lại tab có thể về giữa `message:new` và lúc
 * POST /read xong, mang lại unreadCount cũ.
 */
export function withOpenRead(list: readonly Conversation[], openId: number | null): Conversation[] {
  if (openId === null) return list as Conversation[]
  return list.map((c) => (c.id === openId && c.unreadCount > 0 ? { ...c, unreadCount: 0 } : c))
}
